import { useMemo } from 'react';

import { TEXT_EDITOR_TYPE } from 'src/components/TextDetailsPanel';
import { useScoringMethodName, usePrepareDetectedBrandTerms, useEditorMarks } from 'src/services';
import { useIsProjectReadOnly } from 'src/store';
import { getToolBriefType } from 'src/data/generationTool';

const scoreIsNotAppealing = (score) => score < 60;
export const usePrepareTextDetailsPanelProps = ({
  editorContent,
  textEditorType = TEXT_EDITOR_TYPE.richSlate,
  onEditorContentChange,
  onEditorReferenceChange,
  localEditorRef,
  onCancelChanges,
  onSaveChanges,
  blockedReason,
  isScoreLocked,
  readOnly,
  onScoringMethodClick,
  onApplyDetectedBrandTerm,
  onEditBrandTerm,
  customerHasAccessToPerformance,
  onBusinessBookDemoClick,
  userAllowedToBookDemo,
  brandTerms,
  assetType,
  scoringMethod,
  generationTool,
  updating,
  applyingChanges,
  onLockedUpgradeClick,
  onUndoClick,
  undoDisabled,
  detectedBrandTermsData,
  sensitivityData,
  scoreData,
  histogramsData,
  annotationsData,
  campaignKeywords,
  forceDisabled,
  scoringMethodName,
  secondaryScoringMethodName,
  displayScoringMethod = true,
  textForReadabilityScore,
  markedKeywordsMap,
  applicationMode,
  onEditorCopy,
  editorReadOnly,
  targetKeywordsData,
  relatedKeywordsData,
  onToggleMarkedKeyword,
  onSeoTrackerExpand,
  onBrandRulesExpand,
  plagiarismChecksUsed,
  plagiarismChecksLimit,
  hasPerformedPlagiarismCheck,
  potentialCopiedSentences,
  lastPlagiarismCheckTime,
  loadingPlagiarismReport,
  onCheckPlagiarism,
  textForPlagiarism,
  isOpen,
  plagiarismOnOpenCallback,
}) => {
  const editorMarks = useEditorMarks(detectedBrandTermsData);
  const preparedBrandTerms = usePrepareDetectedBrandTerms(detectedBrandTermsData);
  const isProjectReadOnly = useIsProjectReadOnly();
  const noneAppeal = scoreIsNotAppealing(scoreData?.score);
  const briefType = getToolBriefType(generationTool);
  const disabled = forceDisabled || isProjectReadOnly || updating || applyingChanges;
  const deducedScoringMethodName = useScoringMethodName(briefType, scoringMethod);

  const copyInputProps = useMemo(
    () => ({
      assetType,
      editorContent,
      onEditorContentChange,
      disabled,
      textEditorType,
      requireChangesConfirmation: true,
      editorRef: localEditorRef,
      onCancelChanges,
      onSaveChanges,
      editorMarks,
      applicationMode,
      onEditorCopy,
      readOnly: editorReadOnly,
      onEditorReferenceChange,
    }),
    [
      assetType,
      editorContent,
      onEditorContentChange,
      disabled,
      textEditorType,
      localEditorRef,
      onCancelChanges,
      onSaveChanges,
      editorMarks,
      applicationMode,
      onEditorCopy,
      editorReadOnly,
      onEditorReferenceChange,
    ]
  );

  const scoreDetailsProps = useMemo(
    () => ({
      briefType,
      score: scoreData?.score,
      basedOnValue: scoreData?.basedOn,
      loading: scoreData?.loading,
      blocked: !!blockedReason,
      locked: isScoreLocked,
      readOnly,
      displayScoringMethod,
      scoringMethodName: scoringMethodName || deducedScoringMethodName,
      onScoringMethodClick,
      campaignKeywords,
      noneAppeal,
    }),
    [
      blockedReason,
      briefType,
      campaignKeywords,
      deducedScoringMethodName,
      displayScoringMethod,
      isScoreLocked,
      noneAppeal,
      onScoringMethodClick,
      readOnly,
      scoreData?.basedOn,
      scoreData?.loading,
      scoreData?.score,
      scoringMethodName,
    ]
  );

  const scoreAdditionalFactorsProps = useMemo(
    () => ({
      campaignKeywords,
      loading: annotationsData?.loading,
      headlineType: secondaryScoringMethodName,
    }),
    [annotationsData?.loading, campaignKeywords, secondaryScoringMethodName]
  );

  const histogramAgeScoreProps = useMemo(
    () => ({
      loading: histogramsData?.loading,
      score: scoreData?.score,
      data: histogramsData?.age,
    }),
    [histogramsData?.age, histogramsData?.loading, scoreData?.score]
  );

  const histogramGenderScoreProps = useMemo(
    () => ({
      loading: histogramsData?.loading,
      data: histogramsData?.gender,
    }),
    [histogramsData?.gender, histogramsData?.loading]
  );

  const talkingPointsProps = useMemo(
    () => ({
      annotations: annotationsData?.annotations,
      loading: annotationsData?.loading,
    }),
    [annotationsData?.annotations, annotationsData?.loading]
  );

  const brandTermsProps = useMemo(
    () => ({
      terms: preparedBrandTerms?.items,
      onApplyDetectedBrandTerm,
      onEditBrandTerm,
      loading: preparedBrandTerms?.loading,
      disabled,
      customerHasAccessToPerformance,
      onBusinessBookDemoClick,
      userAllowedToBookDemo,
      hasBrandTerms: brandTerms?.length > 0,
      onExpand: onBrandRulesExpand,
    }),
    [
      brandTerms?.length,
      customerHasAccessToPerformance,
      disabled,
      onApplyDetectedBrandTerm,
      onBrandRulesExpand,
      onBusinessBookDemoClick,
      onEditBrandTerm,
      preparedBrandTerms?.items,
      preparedBrandTerms?.loading,
      userAllowedToBookDemo,
    ]
  );

  const channelPolicyProps = useMemo(
    () => ({
      assetType,
      sensitivity: sensitivityData?.sensitivity,
      loading: sensitivityData?.loading,
      horizontalLayout: true,
      policyTitle: true,
    }),
    [assetType, sensitivityData?.loading, sensitivityData?.sensitivity]
  );

  const blockedProps = useMemo(() => {
    if (!blockedReason) {
      return null;
    }

    return {
      blockedReason,
      onUpgradeClick: onLockedUpgradeClick,
      onUndoClick,
      undoDisabled,
    };
  }, [blockedReason, onLockedUpgradeClick, onUndoClick, undoDisabled]);

  const readabilityScoreProps = useMemo(
    () => ({
      copyText: textForReadabilityScore,
    }),
    [textForReadabilityScore]
  );

  const seoTrackerProps = useMemo(
    () => ({
      targetKeywordsData,
      relatedKeywordsData,
      onToggleMarkedKeyword,
      markedKeywordsMap,
      onExpand: onSeoTrackerExpand,
    }),
    [
      targetKeywordsData,
      relatedKeywordsData,
      onToggleMarkedKeyword,
      markedKeywordsMap,
      onSeoTrackerExpand,
    ]
  );

  const plagiarismCheckerProps = useMemo(
    () => ({
      copyText: textForPlagiarism,
      plagiarismChecksUsed,
      plagiarismChecksLimit,
      loading: loadingPlagiarismReport,
      lastPlagiarismCheckTime,
      hasPerformedPlagiarismCheck,
      onCheckPlagiarism,
      potentialCopiedSentences,
      isOpen,
      plagiarismOnOpenCallback,
    }),
    [
      plagiarismChecksUsed,
      plagiarismChecksLimit,
      lastPlagiarismCheckTime,
      hasPerformedPlagiarismCheck,
      onCheckPlagiarism,
      potentialCopiedSentences,
      loadingPlagiarismReport,
      textForPlagiarism,
      isOpen,
      plagiarismOnOpenCallback,
    ]
  );

  return useMemo(
    () => ({
      copyInputProps,
      scoreDetailsProps,
      histogramAgeScoreProps,
      histogramGenderScoreProps,
      talkingPointsProps,
      brandTermsProps,
      channelPolicyProps,
      blockedProps,
      scoreAdditionalFactorsProps,
      readabilityScoreProps,
      seoTrackerProps,
      plagiarismCheckerProps,
    }),
    [
      copyInputProps,
      scoreDetailsProps,
      histogramAgeScoreProps,
      histogramGenderScoreProps,
      talkingPointsProps,
      brandTermsProps,
      channelPolicyProps,
      blockedProps,
      scoreAdditionalFactorsProps,
      readabilityScoreProps,
      seoTrackerProps,
      plagiarismCheckerProps,
    ]
  );
};
